import type { AuthTokens } from '@i9portal/shared';
import { ApiError, isApiErrorBody, NETWORK_ERROR_CODE, UNKNOWN_ERROR_CODE } from './api-error';
import { API_BASE_URL, API_PREFIX } from './config';
import { toQueryString } from './query-string';
import type {
  ApiClientConfig,
  ApiRequestOptions,
  DownloadResult,
  QueryParams,
  UploadOptions,
} from './types';

const CLIENT_HEADERS = { 'x-client': 'web' } as const;
const CSRF_HEADERS = { 'x-requested-with': 'i9portal' } as const;

type AuthFailureListener = () => void;
type TokenListener = (token: string | null) => void;

/**
 * Cliente HTTP da API do i9Portal.
 *
 * - O access token fica apenas em memória (nunca em localStorage).
 * - O refresh token é um cookie httpOnly enviado apenas para /api/v1/auth.
 * - Em 401 com `TOKEN_EXPIRED`, renova o token uma única vez (single-flight:
 *   requisições concorrentes compartilham a mesma renovação) e repete a requisição.
 * - Se a renovação falhar, notifica os ouvintes de falha de autenticação (→ /login).
 */
export class ApiClient {
  private readonly baseUrl: string;
  private readonly fetchImpl: typeof fetch;
  private readonly xhrFactory: () => XMLHttpRequest;
  private accessToken: string | null = null;
  private refreshPromise: Promise<AuthTokens | null> | null = null;
  private readonly authFailureListeners = new Set<AuthFailureListener>();
  private readonly tokenListeners = new Set<TokenListener>();

  constructor(config: ApiClientConfig) {
    this.baseUrl = config.baseUrl.replace(/\/+$/, '');
    this.fetchImpl = config.fetchImpl ?? ((...args) => globalThis.fetch(...args));
    this.xhrFactory = config.xhrFactory ?? (() => new XMLHttpRequest());
  }

  /* ----------------------------- tokens ----------------------------- */

  getAccessToken(): string | null {
    return this.accessToken;
  }

  setAccessToken(token: string | null): void {
    this.accessToken = token;
    this.tokenListeners.forEach((l) => l(token));
  }

  onTokenChange(listener: TokenListener): () => void {
    this.tokenListeners.add(listener);
    return () => this.tokenListeners.delete(listener);
  }

  onAuthFailure(listener: AuthFailureListener): () => void {
    this.authFailureListeners.add(listener);
    return () => this.authFailureListeners.delete(listener);
  }

  private notifyAuthFailure(): void {
    this.setAccessToken(null);
    this.authFailureListeners.forEach((l) => l());
  }

  url(path: string, query?: QueryParams): string {
    const normalized = path.startsWith('/') ? path : `/${path}`;
    return `${this.baseUrl}${API_PREFIX}${normalized}${toQueryString(query)}`;
  }

  /**
   * Renova o access token usando o cookie httpOnly. Concorrência: todas as chamadas
   * simultâneas recebem a mesma Promise. Retorna `null` se a sessão não puder ser renovada.
   */
  refresh(): Promise<AuthTokens | null> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.performRefresh().finally(() => {
        this.refreshPromise = null;
      });
    }
    return this.refreshPromise;
  }

  private async performRefresh(): Promise<AuthTokens | null> {
    try {
      const response = await this.fetchImpl(this.url('/auth/refresh'), {
        method: 'POST',
        credentials: 'include',
        headers: { ...CLIENT_HEADERS, ...CSRF_HEADERS, 'content-type': 'application/json' },
        body: '{}',
      });
      if (!response.ok) {
        this.setAccessToken(null);
        return null;
      }
      const tokens = (await response.json()) as AuthTokens;
      this.setAccessToken(tokens.accessToken);
      return tokens;
    } catch {
      this.setAccessToken(null);
      return null;
    }
  }

  /* ----------------------------- requests ----------------------------- */

  async request<T>(path: string, options: ApiRequestOptions = {}): Promise<T> {
    const response = await this.send(path, options);
    if (response.status === 204) return undefined as T;
    const text = await safeText(response);
    return (text ? safeJson(text) : undefined) as T;
  }

  get<T>(path: string, query?: QueryParams, signal?: AbortSignal): Promise<T> {
    return this.request<T>(path, { method: 'GET', query, signal });
  }

  post<T>(
    path: string,
    body?: unknown,
    options: Omit<ApiRequestOptions, 'method' | 'body'> = {},
  ): Promise<T> {
    return this.request<T>(path, { ...options, method: 'POST', body });
  }

  patch<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'PATCH', body });
  }

  put<T>(path: string, body?: unknown): Promise<T> {
    return this.request<T>(path, { method: 'PUT', body });
  }

  delete<T = void>(path: string): Promise<T> {
    return this.request<T>(path, { method: 'DELETE' });
  }

  /** Baixa um arquivo autenticado (CSV, PDF, anexo) como Blob. */
  async download(path: string, query?: QueryParams): Promise<DownloadResult> {
    const response = await this.send(path, { method: 'GET', query });
    const blob = await response.blob();
    return {
      blob,
      filename: parseContentDispositionFilename(response.headers.get('content-disposition')),
      contentType: response.headers.get('content-type'),
    };
  }

  /** Executa a requisição com renovação automática do token. Lança ApiError se !ok. */
  private async send(path: string, options: ApiRequestOptions, isRetry = false): Promise<Response> {
    const tokenUsed = this.accessToken;
    let response: Response;
    try {
      response = await this.fetchImpl(this.url(path, options.query), this.buildInit(options));
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') throw error;
      throw new ApiError({
        status: 0,
        code: NETWORK_ERROR_CODE,
        message: 'Não foi possível conectar ao servidor. Verifique sua conexão e tente novamente.',
      });
    }

    if (response.ok) return response;

    const error = await normalizeErrorResponse(response);
    if (response.status === 401 && !options.skipAuthRefresh) {
      if (error.code === 'TOKEN_EXPIRED' && !isRetry) {
        // Outra requisição já renovou o token enquanto esta estava em trânsito.
        if (this.accessToken && this.accessToken !== tokenUsed)
          return this.send(path, options, true);
        const tokens = await this.refresh();
        if (tokens) return this.send(path, options, true);
        this.notifyAuthFailure();
      } else if (error.code !== 'INVALID_CREDENTIALS') {
        // UNAUTHENTICATED, SESSION_REVOKED, TOKEN_INVALID ou token expirado mesmo após renovação.
        this.notifyAuthFailure();
      }
    }
    throw error;
  }

  private buildInit(options: ApiRequestOptions): RequestInit {
    const headers: Record<string, string> = { ...CLIENT_HEADERS, ...(options.headers ?? {}) };
    if (this.accessToken) headers.authorization = `Bearer ${this.accessToken}`;
    let body: BodyInit | undefined;
    if (options.body instanceof FormData) {
      body = options.body;
    } else if (options.body !== undefined) {
      headers['content-type'] = 'application/json';
      body = JSON.stringify(options.body);
    }
    if (options.withCredentials) Object.assign(headers, CSRF_HEADERS);
    return {
      method: options.method ?? 'GET',
      headers,
      body,
      signal: options.signal,
      credentials: options.withCredentials ? 'include' : 'same-origin',
    };
  }

  /* ----------------------------- upload ----------------------------- */

  /**
   * Envia um arquivo via multipart com eventos de progresso (XMLHttpRequest).
   * Renova o token uma vez em caso de `TOKEN_EXPIRED`.
   */
  async upload<T>(
    path: string,
    file: File,
    options: UploadOptions = {},
    isRetry = false,
  ): Promise<T> {
    try {
      return await this.uploadOnce<T>(path, file, options);
    } catch (error) {
      if (
        !isRetry &&
        error instanceof ApiError &&
        error.status === 401 &&
        error.code === 'TOKEN_EXPIRED'
      ) {
        const tokens = await this.refresh();
        if (tokens) return this.upload<T>(path, file, options, true);
        this.notifyAuthFailure();
      }
      throw error;
    }
  }

  private uploadOnce<T>(path: string, file: File, options: UploadOptions): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const xhr = this.xhrFactory();
      xhr.open('POST', this.url(path));
      xhr.setRequestHeader('x-client', CLIENT_HEADERS['x-client']);
      if (this.accessToken) xhr.setRequestHeader('authorization', `Bearer ${this.accessToken}`);
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && options.onProgress) {
          options.onProgress(Math.round((event.loaded / event.total) * 100));
        }
      };
      xhr.onload = () => {
        const text = xhr.responseText;
        const parsed = text ? safeJson(text) : undefined;
        if (xhr.status >= 200 && xhr.status < 300) {
          options.onProgress?.(100);
          resolve(parsed as T);
          return;
        }
        reject(errorFromBody(xhr.status, parsed, xhr.getResponseHeader('x-correlation-id')));
      };
      xhr.onerror = () =>
        reject(
          new ApiError({
            status: 0,
            code: NETWORK_ERROR_CODE,
            message: 'Falha de conexão durante o envio do arquivo.',
          }),
        );
      xhr.onabort = () =>
        reject(new ApiError({ status: 0, code: 'ABORTED', message: 'Envio cancelado.' }));
      if (options.signal) {
        if (options.signal.aborted) {
          xhr.abort();
          return;
        }
        options.signal.addEventListener('abort', () => xhr.abort(), { once: true });
      }
      const form = new FormData();
      form.append(options.fieldName ?? 'file', file, file.name);
      xhr.send(form);
    });
  }
}

/* ----------------------------- helpers ----------------------------- */

async function safeText(response: Response): Promise<string> {
  try {
    return await response.text();
  } catch {
    return '';
  }
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function errorFromBody(
  status: number,
  body: unknown,
  headerCorrelationId: string | null,
): ApiError {
  if (isApiErrorBody(body)) {
    return new ApiError({
      status,
      code: body.error.code,
      message: body.error.message,
      details: body.error.details,
      correlationId: body.error.correlationId ?? headerCorrelationId,
    });
  }
  return new ApiError({
    status,
    code: status >= 500 ? UNKNOWN_ERROR_CODE : `HTTP_${status}`,
    message:
      status >= 500
        ? 'O servidor encontrou um erro inesperado. Tente novamente em instantes.'
        : 'Não foi possível concluir a operação.',
    correlationId: headerCorrelationId,
  });
}

/** Lê o corpo de erro e o converte em ApiError. */
export async function normalizeErrorResponse(response: Response): Promise<ApiError> {
  const text = await safeText(response);
  const body = text ? safeJson(text) : undefined;
  return errorFromBody(response.status, body, response.headers.get('x-correlation-id'));
}

export function parseContentDispositionFilename(header: string | null): string | null {
  if (!header) return null;
  const star = /filename\*=(?:UTF-8'')?([^;]+)/i.exec(header);
  if (star) {
    try {
      return decodeURIComponent(star[1].trim().replace(/^"|"$/g, ''));
    } catch {
      /* segue para o formato simples */
    }
  }
  const plain = /filename="?([^";]+)"?/i.exec(header);
  return plain ? plain[1].trim() : null;
}

/** Instância única usada pela aplicação. */
export const api = new ApiClient({ baseUrl: API_BASE_URL });
